import React, { useState, useEffect } from 'react';
import { supabase } from '../../../config/supabase';
import { ContentBlock, Icon, Tag, Button } from '../../DesignSystem';
import './ReferralCard.css';

interface Referral {
  id: string;
  referred_id: string;
  status: 'pending' | 'completed';
  reward_applied: boolean;
  created_at: string;
  activated_at: string | null;
  // Fetched separately to avoid join issues
  referred_name?: string;
  referred_email?: string;
}

interface ReferralCardProps {
  organizerId: string;
  organizerEmail: string;
}

/**
 * ReferralCard - Display referral stats and list
 * Shows number of active referrals, months earned, and list of referred users
 */
const ReferralCard: React.FC<ReferralCardProps> = ({
  organizerId,
  organizerEmail,
}) => {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isListExpanded, setIsListExpanded] = useState(false);

  useEffect(() => {
    loadReferrals();
  }, [organizerId]);

  const loadReferrals = async () => {
    try {
      // 1. Fetch referrals (without join to avoid foreign key issues)
      const { data: referralData, error: refError } = await (supabase as any)
        .from('referrals')
        .select('id, referred_id, status, reward_applied, created_at, activated_at')
        .eq('referrer_id', organizerId)
        .order('created_at', { ascending: false });

      if (refError || !referralData) {
        console.error('Failed to load referrals:', refError);
        return;
      }

      // 2. Fetch referred organizers info separately
      const referredIds = referralData.map((r: any) => r.referred_id);

      if (referredIds.length > 0) {
        // Use organizers_public view instead of organizers table
        // The table is now restricted to own profile only (RLS security)
        const { data: organizersData } = await (supabase as any)
          .from('organizers_public')
          .select('id, organizer, organizer_contact')
          .in('id', referredIds);

        // 3. Merge data
        const enrichedReferrals = referralData.map((ref: any) => {
          const org = organizersData?.find((o: any) => o.id === ref.referred_id);
          return {
            ...ref,
            referred_name: org?.organizer || 'User',
            referred_email: org?.organizer_contact || '',
          };
        });

        setReferrals(enrichedReferrals);
      } else {
        setReferrals([]);
      }
    } catch (err) {
      console.error('Failed to load referrals:', err);
    } finally {
      setLoading(false);
    }
  };

  // Referrals where filleul is in trial (completed but not yet rewarded)
  const inTrialCount = referrals.filter(r => r.status === 'completed' && !r.reward_applied).length;
  // Referrals where reward was applied (filleul became paying customer)
  const rewardedCount = referrals.filter(r => r.reward_applied === true).length;
  // Total months earned = rewarded referrals
  const totalMonthsEarned = rewardedCount;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(organizerEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <ContentBlock className="referral-card referral-card--loading" border="light">
        <div className="referral-card__skeleton">
          <div className="referral-card__skeleton-header" />
          <div className="referral-card__skeleton-line" />
          <div className="referral-card__skeleton-line" />
        </div>
      </ContentBlock>
    );
  }

  return (
    <ContentBlock className="referral-card" border="light">
      {/* Header */}
      <div className="referral-card__header">
        <Icon name="Users" size={18} />
        <span>Invite friends, get rewards</span>
      </div>

      {/* Stats */}
      <div className="referral-card__stats">
        <div className="referral-card__stat">
          <span className="referral-card__stat-value">{inTrialCount}</span>
          <span className="referral-card__stat-label">In trial</span>
        </div>
        <div className="referral-card__stat">
          <span className="referral-card__stat-value">+{totalMonthsEarned}</span>
          <span className="referral-card__stat-label">Months earned</span>
        </div>
      </div>

      {/* Share section */}
      <div className="referral-card__share">
        <p className="referral-card__share-title">Your referral code:</p>
        <div className="referral-card__code-container">
          <code className="referral-card__code">{organizerEmail}</code>
          <Button
            variant="ghost"
            size="sm"
            onClick={copyToClipboard}
            className="referral-card__copy-btn"
          >
            <Icon name={copied ? "Check" : "Copy"} size={16} />
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </div>
        <p className="referral-card__share-hint">
          Share this email with friends. They'll get 1 month free,
          and you'll get 1 month free when they become a paying customer!
        </p>
      </div>

      {/* Referrals list - collapsible */}
      {referrals.length > 0 && (
        <div className="referral-card__list">
          <button
            type="button"
            className="referral-card__list-header"
            onClick={() => setIsListExpanded(!isListExpanded)}
            aria-expanded={isListExpanded}
          >
            <span className="referral-card__list-title">
              Your referrals ({referrals.length})
            </span>
            <Icon
              name="ChevronDown"
              size={18}
              className={`referral-card__list-chevron ${isListExpanded ? 'referral-card__list-chevron--expanded' : ''}`}
            />
          </button>

          {isListExpanded && (
            <div className="referral-card__list-items">
              {referrals.map(ref => {
                // Determine display status
                let statusLabel: string;
                let statusType: 'published' | 'draft' | 'modified';

                if (ref.reward_applied) {
                  statusLabel = 'Active';
                  statusType = 'published';
                } else if (ref.status === 'completed') {
                  statusLabel = 'In trial';
                  statusType = 'modified';
                } else {
                  statusLabel = 'Pending';
                  statusType = 'draft';
                }

                return (
                  <div key={ref.id} className="referral-card__item">
                    <div className="referral-card__item-info">
                      <span className="referral-card__item-name">
                        {ref.referred_name || 'User'}
                      </span>
                      <span className="referral-card__item-date">
                        {formatDate(ref.created_at)}
                      </span>
                    </div>
                    <Tag
                      label={statusLabel}
                      type="status"
                      status={statusType}
                      size="sm"
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </ContentBlock>
  );
};

export default ReferralCard;
